import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import {
  IDmContentInfo, IDmSnsMsgSetting, IDmSnsMsgSettingDetail, ILineMsgTemplate, ISignedUrl
} from '@blcloud/bl-datamodel';
import { DmSnsMsgBodyTypeDiv } from '@blcloud/bl-datamodel/enum/approach/dm-sns-msg-body-type-div';
import { DmSnsMsgBtnActionDiv, DmSnsMsgBtnActionDivArray } from '@blcloud/bl-datamodel/enum/approach/dm-sns-msg-btn-action-div';
import { FilePublicDiv } from '@blcloud/bl-datamodel/enum/common/file-public-div';
import {
  AttachmentComponent,
  ISurveyGuideLabelValue,
  LineMsgTemplateGuideComponent,
  SurveySettingResourceDisplayService,
} from '@blcloud/bl-ng-share-module';
import {
  BlContextMenuItem,
  BlFileType,
  BlFormGroup,
  BlValidator,
  DropzoneOption,
  Rule,
  ModalReason,
  BlModalService,
  EmojiItem,
  BlDropZoneImageCompressCondition
} from '@blcloud/bl-ng-ui-component';
import {
  cloneDeep as _cloneDeep,
  concat as _concat,
  defer as _defer,
  filter as _filter,
  find as _find,
  findIndex as _findIndex,
  flatMap as _flatMap,
  forEach as _forEach,
  head as _head,
  isArray as _isArray,
  isEmpty as _isEmpty,
  isNil as _isNil,
  map as _map,
  remove as _remove,
  slice as _slice,
  some as _some,
  join as _join
} from 'lodash';
import { Observable } from 'rxjs/Observable';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';
import { of as ObservableOf } from 'rxjs/observable/of';
import { map, mergeMap, takeUntil } from 'rxjs/operators';
import {
  ContextMenuList,
  DefaultDmSnsMsgSetting,
  DefaultDmSnsMsgSettingDetail,
  DefaultDmSnsMsgSettingEx,
  DmSnsMsgBodyTypeItems,
  DmSnsMsgBodyTypeRequiredKeys,
  EmojiInputItemType
} from './sns-send-message-content.const';
import {
  ContextMenu, DmSnsMsgBodyType as SnsMsgBodyType, DmSnsMsgSettingEx, IDmSnsMsgSettingEx
} from './sns-send-message-content.interface';
import { SnsSendMessageContentService } from './sns-send-message-content.service';
import { SnsConst } from '@blcloud/bl-datamodel/const/sns';

/**
 * SNS一斉送信設定コンテンツ
 */
@Component({
  selector: 'app-sns-send-message-content',
  templateUrl: './sns-send-message-content.component.html',
  styleUrls: ['./sns-send-message-content.component.scss'],
  providers: [SnsSendMessageContentService]
})
export class SnsSendMessageContentComponent implements OnInit, OnChanges, OnDestroy {

  /** エンティティ */
  @Input() entity: IDmContentInfo;

  /** フォームグループ */
  @Input() blFormGroup: BlFormGroup;

  /** リッチ版LINE案内文を設定するか */
  @Input() isSettingLineRichTemplate = false;

  /** Web予約サービスが有効か */
  @Input() isAvailableWebReserveService = false;

  /** アンケート基本サービスが有効か */
  @Input() isAvailableSurvey = true;

  /** 組織コード */
  @Input() organizationCode: string;

  /** 他組織参照権限フラグ */
  @Input() isParentOrganization = false;

  /** コンテキストメニューリスト */
  @ViewChildren('contextMenuList') private contextMenuList: QueryList<ElementRef>;

  /** フレックスメッセージセル添付画像 */
  @ViewChildren('flexMsgCellAttachment') private flexMsgCellAttachmentList: QueryList<AttachmentComponent>;

  /** 画像セル添付画像 */
  @ViewChildren('imageCellAttachment') private imageCellAttachmentList: QueryList<AttachmentComponent>;

  /** DMSNSメッセージ設定更新イベント */
  @Output() updateDmSnsMsgSettingEvent = new EventEmitter<DmSnsMsgSettingEx[]>();

  /** 通常テキストセル本文許容桁数 */
  normalMessaageLength = 500;

  /** リッチテンプレートセル件名許容桁数 */
  richMessaageTitleLength = 40;

  /** リッチテンプレートセル件名2許容桁数 */
  richMessaageTitle2Length = 40;

  /** リッチテンプレートセル件名3許容桁数 */
  richMessaageTitle3Length = 40;

  /** リッチテンプレート本文許容桁数 */
  richMessaageLength = 200;

  /** LINEメッセージテンプレートリスト */
  lineMsgTemplateList: ILineMsgTemplate[] = [];

  /** LINEメッセージテンプレートの画像URLリスト */
  private _lineMsgTemplateUrlList: ISignedUrl[] = [];

  /** DMSNSメッセージ本文タイプ選択肢 */
  _dmSnsMsgBodyTypeItems = DmSnsMsgBodyTypeItems;

  /** DMSNSメッセージ本文タイプ定義 */
  readonly DmSnsMsgBodyType = SnsMsgBodyType;

  /** DMSNSメッセージボタンアクション区分 */
  _dmSnsMsgBtnActionDivArray = DmSnsMsgBtnActionDivArray;

  /** DMSNSメッセージボタンアクション区分 */
  readonly DmSnsMsgBtnActionDiv = DmSnsMsgBtnActionDiv;

  /** 絵文字入力項目タイプ */
  readonly EmojiInputItemType = EmojiInputItemType;

  /** 絵文字入力結果 */
  private insertEmojiResult: { text: string, start: number, end: number } = { text: '', start: 0, end: 0 };

  /** 画像オプション */
  readonly shareImageOption: DropzoneOption = {
    accept: [BlFileType.IMAGE],
    maxSize: 1048576,
    imageCompressCondition: BlDropZoneImageCompressCondition.WhenSizeOver,
    compress: {
      iteration: true,
      maxIteration: 50
    }
  };

  /** DMSNSメッセージ設定拡張配列 */
  _dmSnsMsgSettingExList: DmSnsMsgSettingEx[] = [];

  /** ルールバックアップ */
  _ruleBackup: Array<{ key: string, rule: Rule }> = [];

  private readonly onDestroy$ = new EventEmitter();

  /** コンストラクタ */
  constructor(
    private service: SnsSendMessageContentService,
    private modalService: BlModalService,
    private surveySettingResourceDisplay: SurveySettingResourceDisplayService,
  ) {
  }

  ngOnDestroy() {
    this.onDestroy$.emit();
  }

  /**
   * 初期処理
   */
  ngOnInit() {
    this.service.getLineMsgTemplate().subscribe(res => {
      this.lineMsgTemplateList = res;
      // テンプレートイメージURLリスト取得
      this.service.getLineTemplateImageUrl().subscribe(
        signedUrlList => {
          this._lineMsgTemplateUrlList = signedUrlList;
          this.setLineMsgTemplateInfoInFlex();
          this.setInputPosition();
        },
        () => {
          // エラーのときも後続処理を実行する
          this.setLineMsgTemplateInfoInFlex();
          this.setInputPosition();
        });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!_isNil(changes.isAvailableWebReserveService)) {
      // 画面からの@Inputの引き渡しのタイミングを考慮しサービスIDの有効可否をここで判定
      if (!this.isAvailableWebReserveService) {
        this._dmSnsMsgBtnActionDivArray = _filter(
          this._dmSnsMsgBtnActionDivArray, dmSnsMsgBtnActionDiv => {
            // Web予約サービスが無効の場合はボタンアクション「予約メニュー表示」を非表示
            return dmSnsMsgBtnActionDiv.value !== DmSnsMsgBtnActionDiv.ReserveMenuDisp;
          }
        );
      } else {
        this._dmSnsMsgBtnActionDivArray = DmSnsMsgBtnActionDivArray;
      }
    }

    if (!_isNil(changes.isAvailableSurvey)) {
      this.updateDmSnsMsgBodyTypeItems();
    }

    // ルール保存
    if (!_isNil(changes.blFormGroup)) {
      const keys = _filter(Object.keys(this.blFormGroup.validatorGroup), key =>
        key.startsWith('DmSnsMsgSetting#') || key.startsWith('DmSnsMsgSettingDetail#')
      );
      this._ruleBackup = _map(keys, key => {
        return { key, rule: this.blFormGroup.getValidator(key).getRule() };
      });
    }
    if ((!_isNil(changes.entity) || !_isNil(changes.isSettingLineRichTemplate)
      || !_isNil(changes.blFormGroup) || !_isNil(changes.isAvailableSurvey)) && this.entity) {
      if (_isNil(this.entity.dmSnsMsgSettingList)) {
        this.entity.dmSnsMsgSettingList = [];
      }

      if (this.isSettingLineRichTemplate) {
        // dmSnsMsgSettingListが0件の場合、1件データ挿入
        if (_isEmpty(this.entity.dmSnsMsgSettingList)) {
          const data = _cloneDeep(DefaultDmSnsMsgSetting);
          data.itemNumber = 1;
          this.entity.dmSnsMsgSettingList.push(data);
        }
        // dmSnsMsgSettingDetailListが0件の場合、1件データ挿入
        for (const dmSnsMsgSetting of this.entity.dmSnsMsgSettingList) {
          if (dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.FlexMessage) {
            if (_isEmpty(dmSnsMsgSetting.dmSnsMsgSettingDetailList)) {
              dmSnsMsgSetting.dmSnsMsgSettingDetailList.push(_cloneDeep(DefaultDmSnsMsgSettingDetail));
            }
          }
        }
      }
      if (this.isSettingLineRichTemplate) {
        // 拡張配列生成
        this._dmSnsMsgSettingExList = _map(this.entity.dmSnsMsgSettingList, dmSnsMsgSetting => {
          const setting = <IDmSnsMsgSettingEx>dmSnsMsgSetting;
          return {
            ..._cloneDeep(DefaultDmSnsMsgSettingEx),
            dmSnsMsgSetting: setting,
            survey: {
              surveyNo: setting.surveyNo || null,
              surveyName: setting.surveyName || '',
            },
          } as DmSnsMsgSettingEx;
        });
        this.setEnabledItemInFlex();
        this.setInputPosition();
        this.setLineMsgTemplateInfoInFlex();

        // 文字数カウント更新
        this.updateWordCount();
        if (!this.isAvailableSurvey) {
          this.convertSurveyItemsToText();
        } else {
          this.resolveUnknownSurveyNames();
        }
      } else {
        // 件名+本文の案内文では吹き出しを使わないため、リッチ版で生成した設定を残さない
        this._dmSnsMsgSettingExList = [];
      }
      this.setValidator();
      this.notifyChange();
    }
  }

  /**
   * アンケート基本サービスの有無に応じて本文タイプ選択肢を更新する
   */
  private updateDmSnsMsgBodyTypeItems() {
    this._dmSnsMsgBodyTypeItems = this.isAvailableSurvey
      ? DmSnsMsgBodyTypeItems
      : _filter(DmSnsMsgBodyTypeItems, item => item.code !== SnsMsgBodyType.Survey);
  }

  /**
   * アンケート名が不明な吹き出しを、アンケート番号でアンケート設定を検索して表示へ反映する
   */
  private resolveUnknownSurveyNames() {
    _forEach(this._dmSnsMsgSettingExList, ex => {
      if (ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv !== SnsMsgBodyType.Survey) {
        return;
      }
      const surveyNo = ex.survey ? ex.survey.surveyNo : null;
      const surveyName = ex.survey ? ex.survey.surveyName : '';
      if (_isNil(surveyNo) || !_isEmpty(surveyName)) {
        return;
      }
      this.surveySettingResourceDisplay.getSurveySettingBySurveyNo(surveyNo).pipe(
        takeUntil(this.onDestroy$)
      ).subscribe(surveySetting => {
        const current = _find(this._dmSnsMsgSettingExList, item =>
          item.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === SnsMsgBodyType.Survey
          && !_isNil(item.survey) && item.survey.surveyNo === surveyNo
        );
        if (_isNil(current) || _isNil(current.survey) || !_isEmpty(current.survey.surveyName)) {
          return;
        }
        if (_isNil(surveySetting) || _isEmpty(surveySetting.surveyName)) {
          return;
        }
        current.survey = {
          surveyNo: surveySetting.surveyNo,
          surveyName: surveySetting.surveyName,
        };
        current.dmSnsMsgSetting.surveyName = surveySetting.surveyName;
        this.notifyChange();
      });
    });
  }

  /**
   * アンケート基本サービスが無効の場合、アンケート本文タイプをテキストへ戻す
   */
  private convertSurveyItemsToText() {
    _forEach(this._dmSnsMsgSettingExList, (ex, index) => {
      if (ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === SnsMsgBodyType.Survey) {
        ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv = DmSnsMsgBodyTypeDiv.Text;
        this.clearSurvey(index, ex.dmSnsMsgSetting);
      }
    });
  }

  /** バリデーション設定 */
  private setValidator() {
    if (!this.isSettingLineRichTemplate) {
      return;
    }
    try {
      // バリデーション設定
      const keys = _filter(Object.keys(this.blFormGroup.validatorGroup), key =>
        key.startsWith('DmSnsMsgSetting#') || key.startsWith('DmSnsMsgSettingDetail#')
      );
      for (const key of keys) {
        const validator = this.blFormGroup.getValidator(key);
        const validators = _map(new Array(this._dmSnsMsgSettingExList.length), () => validator.dupe(_cloneDeep(validator.getRule())));
        this.blFormGroup.setArrayValidator(key, validators);
      }
      this.applyRequiredInputDiv();
    } catch (e) {
      // setArrayValidator実行済みの場合例外となるため無視する。一度実行されれば問題なし。
    }
  }

  /**
   * 本文タイプ：フレックスメッセージのセルについて入力可能項目を設定
   */
  private setEnabledItemInFlex() {
    this._dmSnsMsgSettingExList.forEach(ex => {
      if (ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.FlexMessage) {
        // LINEメッセージテンプレート
        const lineMsgTemplate = _find(this.lineMsgTemplateList, v =>
          v.lineMsgTemplateType === ex.dmSnsMsgSetting.lineMsgTemplateType);
        if (lineMsgTemplate && !_isEmpty(lineMsgTemplate.structuralElementNameList)) {
          // 構成要素名配列
          const structuralElementNameList: String[] = lineMsgTemplate.structuralElementNameList;
          // 件名
          ex.isEnabledSetTitleInFlex = _some(structuralElementNameList, item => {
            return item === SnsConst.LINE_MSG_TEMPLATE_ITEM_TITLE;
          });
          // 件名2
          ex.isEnabledSetTitle2InFlex = _some(structuralElementNameList, item => {
            return item === SnsConst.LINE_MSG_TEMPLATE_ITEM_TITLE2;
          });
          // 件名3
          ex.isEnabledSetTitle3InFlex = _some(structuralElementNameList, item => {
            return item === SnsConst.LINE_MSG_TEMPLATE_ITEM_TITLE3;
          });
          // ラベル
          ex.isEnabledSetLabelInFlex = _some(structuralElementNameList, item => {
            return item === SnsConst.LINE_MSG_TEMPLATE_ITEM_LABEL;
          });
          // ラベル2
          ex.isEnabledSetLabel2InFlex = _some(structuralElementNameList, item => {
            return item === SnsConst.LINE_MSG_TEMPLATE_ITEM_LABEL2;
          });
          // 本文
          ex.isEnabledSetBodyInFlex = _some(structuralElementNameList, item => {
            return item === SnsConst.LINE_MSG_TEMPLATE_ITEM_BODY;
          });
          // フッター
          ex.isEnabledSetFooterInFlex = _some(structuralElementNameList, item => {
            return item === SnsConst.LINE_MSG_TEMPLATE_ITEM_FOOTER;
          });
          // フッター2
          ex.isEnabledSetFooter2InFlex = _some(structuralElementNameList, item => {
            return item === SnsConst.LINE_MSG_TEMPLATE_ITEM_FOOTER2;
          });
          // 画像
          ex.isEnabledSetImageInFlex = _some(structuralElementNameList, item => {
            return item === SnsConst.LINE_MSG_TEMPLATE_ITEM_IMAGE;
          });
        } else {
          // 件名
          ex.isEnabledSetTitleInFlex = false;
          // 件名2
          ex.isEnabledSetTitle2InFlex = false;
          // 件名3
          ex.isEnabledSetTitle3InFlex = false;
          // ラベル
          ex.isEnabledSetLabelInFlex = false;
          // ラベル2
          ex.isEnabledSetLabel2InFlex = false;
          // 本文
          ex.isEnabledSetBodyInFlex = false;
          // フッター
          ex.isEnabledSetFooterInFlex = false;
          // フッター2
          ex.isEnabledSetFooter2InFlex = false;
          // 画像
          ex.isEnabledSetImageInFlex = false;
        }
      }
    });
  }

  /**
   * 本文タイプ：フレックスメッセージのセルにLINEメッセージテンプレートの情報を反映する
   */
  private setLineMsgTemplateInfoInFlex() {
    this._dmSnsMsgSettingExList.forEach((ex) => {
      if (ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.FlexMessage) {
        if (!_isEmpty(ex.dmSnsMsgSetting.lineMsgTemplateType)) {
          const lineMsgTemplate = _find(this.lineMsgTemplateList, v =>
            v.lineMsgTemplateType === ex.dmSnsMsgSetting.lineMsgTemplateType);
          if (!_isNil(lineMsgTemplate)) {
            // 表示名
            ex.dispLineMsgTemplateName = lineMsgTemplate.dispLineMsgTemplateName;
            if (!_isEmpty(lineMsgTemplate.gcsObjectName) &&
              !_isEmpty(this._lineMsgTemplateUrlList)) {
              const subImagePath
                = lineMsgTemplate.gcsObjectName.replace('/fm_sample/', '/fm_sample_sub/')
                  .replace('.png', '_sub.png');
              const subImageUrl = _find(this._lineMsgTemplateUrlList, url =>
                url.filePath === subImagePath);
              // URL
              if (!_isEmpty(subImageUrl)) {
                ex.lineMsgTemplateImageUrl = subImageUrl.signedUrl;
              }
            }
          }
        }
      }
    });
  }

  /**
   * 入力開始位置を設定する
   */
  private setInputPosition() {
    this._dmSnsMsgSettingExList.forEach(ex => {
      if (ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Text) {
        if (!_isNil(ex.dmSnsMsgSetting.dmSnsMsgBody)) {
          // 入力済の場合、絵文字の初期挿入位置は末尾にする
          ex.normalTextInputViewLastSelectionStart = ex.dmSnsMsgSetting.dmSnsMsgBody.length;
          ex.normalTextInputViewLastSelectionEnd = ex.normalTextInputViewLastSelectionStart;
        }
      }
      if (ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.FlexMessage) {
        const detail = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList);
        if (!_isNil(detail.dmSnsMsgTitle)) {
          // 入力済の場合、絵文字の初期挿入位置は末尾にする
          ex.flexTitleInputViewLastSelectionStart = detail.dmSnsMsgTitle.length;
          ex.flexTitleInputViewLastSelectionEnd = ex.flexTitleInputViewLastSelectionStart;
        }
        if (!_isNil(detail.dmSnsMsgBody)) {
          // 入力済の場合、絵文字の初期挿入位置は末尾にする
          ex.flexTextInputViewLastSelectionStart = detail.dmSnsMsgBody.length;
          ex.flexTextInputViewLastSelectionEnd = ex.flexTextInputViewLastSelectionStart;
        }
      }
    });
  }

  /** 入力必須項目を設定 */
  private applyRequiredInputDiv() {
    this.blFormGroup.reset();
    const allKeys = Array.from(new Set(_flatMap(DmSnsMsgBodyTypeRequiredKeys, v => v.keys)));
    _forEach(this._dmSnsMsgSettingExList, (ex, index) => {
      const findKey = _find(DmSnsMsgBodyTypeRequiredKeys, v => v.code === ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv);
      const requiredKeys = findKey ? findKey.keys : [];

      // 案内文のパターンごとに処理
      _forEach(allKeys, key => this.updateRule(key, index, _some(requiredKeys, v => v === key)));

      if (ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.FlexMessage) {
        // 画像アクション部分の必須設定
        const dmSnsMsgImageActionValidFlag = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgImageActionValidFlag;
        if (dmSnsMsgImageActionValidFlag) {
          const dmSnsMsgImageActionDiv = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgImageActionDiv;
          this.updateRule('DmSnsMsgSettingDetail#dmSnsMsgImageSendText', index, dmSnsMsgImageActionDiv === DmSnsMsgBtnActionDiv.Text);
          this.updateRule('DmSnsMsgSettingDetail#dmSnsMsgImageTransitionUrl', index, dmSnsMsgImageActionDiv === DmSnsMsgBtnActionDiv.Url);
        }
        // ボタン部分の必須設定
        const dmSnsMsgBtnDispFlag = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgBtnDispFlag;
        this.updateRule('DmSnsMsgSettingDetail#dmSnsMsgBtnDispName', index, dmSnsMsgBtnDispFlag);
        if (dmSnsMsgBtnDispFlag) {
          const dmSnsMsgBtnActionDiv = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgBtnActionDiv;
          this.updateRule('DmSnsMsgSettingDetail#dmSnsMsgBtnSendText', index, dmSnsMsgBtnActionDiv === DmSnsMsgBtnActionDiv.Text);
          this.updateRule('DmSnsMsgSettingDetail#dmSnsMsgTransitionUrl', index, dmSnsMsgBtnActionDiv === DmSnsMsgBtnActionDiv.Url);
        }
      }
    });
  }

  /**
   * LINEメッセージテンプレートイメージ画像のURLリストを最新化する
   */
  private updateLineMsgTemplateUrlList() {
    // テンプレートイメージURLリスト取得
    this.service.getLineTemplateImageUrl().subscribe(
      signedUrlList => {
        this._lineMsgTemplateUrlList = signedUrlList;
      },
      () => {
        // エラーのときも後続処理を実行する
      });
  }

  /**
   * 文字数カウントを最新化する
   */
  private updateWordCount() {
    _forEach(_filter(this._dmSnsMsgSettingExList,
      ex => ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Text && _isArray(ex.dmSnsMsgSetting.dmSnsMsgBody)),
      ex => {
        ex.count = ex.dmSnsMsgSetting.dmSnsMsgBody.length;
        ex.isStyleDisabled = ex.count < this.normalMessaageLength;
      });
    _forEach(_filter(this._dmSnsMsgSettingExList,
      ex => ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.FlexMessage &&
        _isArray(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList) &&
        ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList.length > 0),
      ex => {
        if (!_isEmpty(_head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgTitle)) {
          ex.titleCount = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgTitle.length;
          ex.isTitleStyleDisabled = ex.titleCount < this.richMessaageTitleLength;
        }
        if (!_isEmpty(_head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgTitle2)) {
          ex.title2Count = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgTitle2.length;
          ex.isTitle2StyleDisabled = ex.title2Count < this.richMessaageTitle2Length;
        }
        if (!_isEmpty(_head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgTitle3)) {
          ex.title3Count = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgTitle3.length;
          ex.isTitle3StyleDisabled = ex.title3Count < this.richMessaageTitle3Length;
        }
        if (!_isEmpty(_head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgBody)) {
          ex.count = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgBody.length;
          ex.isStyleDisabled = ex.count < this.richMessaageLength;
        }
      });
  }

  /**
   * ルール更新
   * @param property プロパティ名
   * @param index インデックス
   * @param requiredInputDiv 必須設定
   */
  private updateRule(property: string, index: number, requiredInputDiv: boolean) {
    const validators = this.blFormGroup.getArrayValidator(property);
    if (!_isEmpty(validators) && validators.length > index) {
      const rule = _cloneDeep(validators[index].getRule());
      rule.requiredInputDiv = requiredInputDiv;
      validators[index].updateRule(rule);
    }
  }

  /**
   * テキストエリア入力イベント (通常テキスト)
   */
  onInputNormalTextarea(event: Event, dmSnsMsgSettingEx: DmSnsMsgSettingEx) {
    dmSnsMsgSettingEx.count = (<HTMLInputElement>event.target).value.length;
    dmSnsMsgSettingEx.isStyleDisabled = dmSnsMsgSettingEx.count <= this.normalMessaageLength;
  }

  /**
   * 件名テキストエリア入力イベント (リッチテンプレート)
   */
  onInputTitleRichTextarea(event: Event, dmSnsMsgSettingEx: DmSnsMsgSettingEx) {
    dmSnsMsgSettingEx.titleCount = (<HTMLInputElement>event.target).value.length;
    dmSnsMsgSettingEx.isTitleStyleDisabled = dmSnsMsgSettingEx.titleCount <= this.richMessaageTitleLength;
  }

  /**
   * 件名2テキストエリア入力イベント (リッチテンプレート)
   */
  onInputTitle2RichTextarea(event: Event, dmSnsMsgSettingEx: DmSnsMsgSettingEx) {
    dmSnsMsgSettingEx.title2Count = (<HTMLInputElement>event.target).value.length;
    dmSnsMsgSettingEx.isTitle2StyleDisabled = dmSnsMsgSettingEx.title2Count <= this.richMessaageTitle2Length;
  }

  /**
   * 件名3テキストエリア入力イベント (リッチテンプレート)
   */
  onInputTitle3RichTextarea(event: Event, dmSnsMsgSettingEx: DmSnsMsgSettingEx) {
    dmSnsMsgSettingEx.title3Count = (<HTMLInputElement>event.target).value.length;
    dmSnsMsgSettingEx.isTitle3StyleDisabled = dmSnsMsgSettingEx.title3Count <= this.richMessaageTitle3Length;
  }

  /**
   * 本文テキストエリア入力イベント (リッチテンプレート)
   */
  onInputRichTextarea(event: Event, dmSnsMsgSettingEx: DmSnsMsgSettingEx) {
    dmSnsMsgSettingEx.count = (<HTMLInputElement>event.target).value.length;
    dmSnsMsgSettingEx.isStyleDisabled = dmSnsMsgSettingEx.count <= this.richMessaageLength;
  }

  /**
   * 件名変更イベント (リッチテンプレート)
   */
  onChangeRichTitle(inputText: Event, ex: DmSnsMsgSettingEx) {
    if (!_isEmpty(inputText)) {
      // 改行コードを削除して反映
      ex.dmSnsMsgSetting.dmSnsMsgNotifyDispTitle
        = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgTitle.replace('\n', '');
      this.notifyChange();
    }
  }

  /**
   * 絵文字入力エリア ブラーイベント
   * @param _event イベント
   */
  onBlurEmojiInputView(_event: FocusEvent, ex: DmSnsMsgSettingEx, type: EmojiInputItemType ) {
    const inputElement = <HTMLInputElement>_event.target;
    switch (type) {
      case EmojiInputItemType.NormalText:
        // ブラー時の最終選択範囲を記憶
        ex.normalTextInputViewLastSelectionStart = inputElement.selectionStart;
        ex.normalTextInputViewLastSelectionEnd = inputElement.selectionEnd;
        break;
      case EmojiInputItemType.FlexTitle:
        // ブラー時の最終選択範囲を記憶
        ex.flexTitleInputViewLastSelectionStart = inputElement.selectionStart;
        ex.flexTitleInputViewLastSelectionEnd = inputElement.selectionEnd;
        break;

      case EmojiInputItemType.FlexTitle2:
        // ブラー時の最終選択範囲を記憶
        ex.flexTitle2InputViewLastSelectionStart = inputElement.selectionStart;
        ex.flexTitle2InputViewLastSelectionEnd = inputElement.selectionEnd;
        break;
      case EmojiInputItemType.FlexTitle3:
        // ブラー時の最終選択範囲を記憶
        ex.flexTitle3InputViewLastSelectionStart = inputElement.selectionStart;
        ex.flexTitle3InputViewLastSelectionEnd = inputElement.selectionEnd;
        break;
      case EmojiInputItemType.FlexLabel:
        // ブラー時の最終選択範囲を記憶
        ex.flexLabelInputViewLastSelectionStart = inputElement.selectionStart;
        ex.flexLabelInputViewLastSelectionEnd = inputElement.selectionEnd;
        break;
      case EmojiInputItemType.FlexLabel2:
        // ブラー時の最終選択範囲を記憶
        ex.flexLabel2InputViewLastSelectionStart = inputElement.selectionStart;
        ex.flexLabel2InputViewLastSelectionEnd = inputElement.selectionEnd;
        break;
      case EmojiInputItemType.FlexFooter:
        // ブラー時の最終選択範囲を記憶
        ex.flexFooterInputViewLastSelectionStart = inputElement.selectionStart;
        ex.flexFooterInputViewLastSelectionEnd = inputElement.selectionEnd;
        break;
      case EmojiInputItemType.FlexFooter2:
        // ブラー時の最終選択範囲を記憶
        ex.flexFooter2InputViewLastSelectionStart = inputElement.selectionStart;
        ex.flexFooter2InputViewLastSelectionEnd = inputElement.selectionEnd;
        break;
      case EmojiInputItemType.FlexText:
        // ブラー時の最終選択範囲を記憶
        ex.flexTextInputViewLastSelectionStart = inputElement.selectionStart;
        ex.flexTextInputViewLastSelectionEnd = inputElement.selectionEnd;
        break;
    }
  }

  /**
   * 絵文字選択イベント
   */
  onSelectEmoji(event: EmojiItem, ex: DmSnsMsgSettingEx, type: EmojiInputItemType) {
    let detail = null;
    switch (type) {
      case EmojiInputItemType.NormalText:
        this.insertEmoji(
          ex.dmSnsMsgSetting.dmSnsMsgBody,
          ex.normalTextInputViewLastSelectionStart,
          ex.normalTextInputViewLastSelectionEnd,
          event.character);
        ex.dmSnsMsgSetting.dmSnsMsgBody = this.insertEmojiResult.text;
        ex.normalTextInputViewLastSelectionStart = this.insertEmojiResult.start;
        ex.normalTextInputViewLastSelectionEnd = this.insertEmojiResult.end;
        ex.count = ex.dmSnsMsgSetting.dmSnsMsgBody.length;
        ex.isStyleDisabled = ex.count < this.normalMessaageLength;
        break;
      case EmojiInputItemType.FlexTitle:
        detail = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList);
        this.insertEmoji(
          detail.dmSnsMsgTitle,
          ex.flexTitleInputViewLastSelectionStart,
          ex.flexTitleInputViewLastSelectionEnd,
          event.character);
        detail.dmSnsMsgTitle = this.insertEmojiResult.text;
        ex.flexTitleInputViewLastSelectionStart = this.insertEmojiResult.start;
        ex.flexTitleInputViewLastSelectionEnd = this.insertEmojiResult.end;
        ex.titleCount = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgTitle.length;
        ex.isTitleStyleDisabled = ex.titleCount < this.richMessaageTitleLength;
        // プッシュ通知用表示タイトルに同期
        ex.dmSnsMsgSetting.dmSnsMsgNotifyDispTitle = detail.dmSnsMsgTitle;
        break;
      case EmojiInputItemType.FlexTitle2:
        detail = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList);
        this.insertEmoji(
          detail.dmSnsMsgTitle2,
          ex.flexTitle2InputViewLastSelectionStart,
          ex.flexTitle2InputViewLastSelectionEnd,
          event.character);
        detail.dmSnsMsgTitle2 = this.insertEmojiResult.text;
        ex.flexTitle2InputViewLastSelectionStart = this.insertEmojiResult.start;
        ex.flexTitle2InputViewLastSelectionEnd = this.insertEmojiResult.end;
        ex.title2Count = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgTitle2.length;
        ex.isTitle2StyleDisabled = ex.title2Count < this.richMessaageTitle2Length;
        break;
      case EmojiInputItemType.FlexTitle3:
        detail = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList);
        this.insertEmoji(
          detail.dmSnsMsgTitle3,
          ex.flexTitle3InputViewLastSelectionStart,
          ex.flexTitle3InputViewLastSelectionEnd,
          event.character);
        detail.dmSnsMsgTitle3 = this.insertEmojiResult.text;
        ex.flexTitle3InputViewLastSelectionStart = this.insertEmojiResult.start;
        ex.flexTitle3InputViewLastSelectionEnd = this.insertEmojiResult.end;
        ex.title3Count = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgTitle3.length;
        ex.isTitle3StyleDisabled = ex.title3Count < this.richMessaageTitle3Length;
        break;
      case EmojiInputItemType.FlexLabel:
        detail = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList);
        this.insertEmoji(
          detail.dmSnsMsgLabel,
          ex.flexLabelInputViewLastSelectionStart,
          ex.flexLabelInputViewLastSelectionEnd,
          event.character);
        detail.dmSnsMsgLabel = this.insertEmojiResult.text;
        ex.flexLabelInputViewLastSelectionStart = this.insertEmojiResult.start;
        ex.flexLabelInputViewLastSelectionEnd = this.insertEmojiResult.end;
        break;
      case EmojiInputItemType.FlexLabel2:
        detail = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList);
        this.insertEmoji(
          detail.dmSnsMsgLabel2,
          ex.flexLabel2InputViewLastSelectionStart,
          ex.flexLabel2InputViewLastSelectionEnd,
          event.character);
        detail.dmSnsMsgLabel2 = this.insertEmojiResult.text;
        ex.flexLabel2InputViewLastSelectionStart = this.insertEmojiResult.start;
        ex.flexLabel2InputViewLastSelectionEnd = this.insertEmojiResult.end;
        break;
      case EmojiInputItemType.FlexFooter:
        detail = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList);
        this.insertEmoji(
          detail.dmSnsMsgFooter,
          ex.flexFooterInputViewLastSelectionStart,
          ex.flexFooterInputViewLastSelectionEnd,
          event.character);
        detail.dmSnsMsgFooter = this.insertEmojiResult.text;
        ex.flexFooterInputViewLastSelectionStart = this.insertEmojiResult.start;
        ex.flexFooterInputViewLastSelectionEnd = this.insertEmojiResult.end;
        break;
      case EmojiInputItemType.FlexFooter2:
        detail = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList);
        this.insertEmoji(
          detail.dmSnsMsgFooter2,
          ex.flexFooter2InputViewLastSelectionStart,
          ex.flexFooter2InputViewLastSelectionEnd,
          event.character);
        detail.dmSnsMsgFooter2 = this.insertEmojiResult.text;
        ex.flexFooter2InputViewLastSelectionStart = this.insertEmojiResult.start;
        ex.flexFooter2InputViewLastSelectionEnd = this.insertEmojiResult.end;
        break;
      case EmojiInputItemType.FlexText:
        detail = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList);
        this.insertEmoji(
          detail.dmSnsMsgBody,
          ex.flexTextInputViewLastSelectionStart,
          ex.flexTextInputViewLastSelectionEnd,
          event.character);
        detail.dmSnsMsgBody = this.insertEmojiResult.text;
        ex.flexTextInputViewLastSelectionStart = this.insertEmojiResult.start;
        ex.flexTextInputViewLastSelectionEnd = this.insertEmojiResult.end;
        ex.count = _head(ex.dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgBody.length;
        ex.isStyleDisabled = ex.count < this.richMessaageLength;
        break;
    }
    this.notifyChange();
  }

  /**
   * 絵文字挿入処理
   */
  private insertEmoji(message: string, selectionStart: number, selectionEnd: number, character: string) {
    let currentText: string = message || '';
    let start = selectionStart;
    let end = selectionEnd;
    const prefix = start > 0 ? _join(_slice(currentText, 0, start), '') : '';
    const suffix = end < currentText.length ? _join(_slice(currentText, end, currentText.length), '') : '';
    // 直前の入力選択位置に絵文字を置換
    currentText = prefix + character + suffix;
    // フォーカス位置を置換後の終了位置に移動しておく
    // 絵文字は基本的に2文字扱いだが変化する可能性もあるため、取得した絵文字の長さ分だけ移動させるようにする
    start += character.length;
    end = start;
    this.insertEmojiResult = { text: currentText, start: start, end: end };
  }

  /**
   * コンテキストメニューを取得します。
   * @param index インデックス
   */
  getContextMenuList(index: number): BlContextMenuItem[] {
    return _filter(_cloneDeep(ContextMenuList), (item: BlContextMenuItem) =>
      item.id === ContextMenu.Up && index !== 0 ||
      item.id === ContextMenu.Down && index !== this._dmSnsMsgSettingExList.length - 1 ||
      item.id === ContextMenu.Add && this._dmSnsMsgSettingExList.length < 3 ||
      item.id === ContextMenu.Delete && this._dmSnsMsgSettingExList.length > 1
    );
  }

  /**
   * コンテキストメニュー選択イベントハンドラ
   * @param menuItem 選択されたアイテム
   * @param index 削除インデックス
   */
  onSelectMenu(menuItem: BlContextMenuItem, index: number) {
    switch (menuItem.id) {
      case ContextMenu.Add:
        this.addDmSnsMsgSetting(index);
        break;
      case ContextMenu.Delete:
        this.removeDmSnsMsgSetting(index);
        break;
      case ContextMenu.Up:
        this.moveUpDmSnsMsgSetting(index);
        break;
      case ContextMenu.Down:
        this.moveDownDmSnsMsgSetting(index);
        break;
    }
  }

  /**
   * 項目の追加を行います。
   * @param index インデックス
   */
  private addDmSnsMsgSetting(index: number) {
    this._dmSnsMsgSettingExList = _forEach(_concat(
      _slice(this._dmSnsMsgSettingExList, 0, index + 1),
      _cloneDeep(DefaultDmSnsMsgSettingEx),
      _slice(this._dmSnsMsgSettingExList, index + 1)
    ), (v, idx) => v.dmSnsMsgSetting.itemNumber = idx + 1);

    // バリデーション設定
    const keys = _filter(Object.keys(this.blFormGroup.validatorGroup), key =>
      key.startsWith('DmSnsMsgSetting#') || key.startsWith('DmSnsMsgSettingDetail#')
    );
    for (const key of keys) {
      const validators = this.blFormGroup.getArrayValidator(key);
      const rule = _find(this._ruleBackup, v => v.key === key);
      if (rule) {
        const validatorList = _concat(
          _slice(validators, 0, index + 1),
          new BlValidator(_cloneDeep(rule.rule)),
          _slice(validators, index + 1)
        );
        this.blFormGroup.setArrayValidator(key, validatorList);
      }
    }
    this.applyRequiredInputDiv();
    this.notifyChange();

    _defer(() => {
      const menu = this.contextMenuList.find((_, idx) => idx === index + 1);
      if (!_isNil(menu)) {
        menu.nativeElement.focus();
      }
    });
  }

  /**
   * 項目の削除を行います。
   * @param index インデックス
   */
  private removeDmSnsMsgSetting(index: number) {
    // バリデーション設定
    const keys = _filter(Object.keys(this.blFormGroup.validatorGroup), key =>
      key.startsWith('DmSnsMsgSetting#') || key.startsWith('DmSnsMsgSettingDetail#')
    );
    for (const key of keys) {
      this.blFormGroup.removeArrayValidator([key], index);
    }
    _remove(this._dmSnsMsgSettingExList, (_, idx) => idx === index);
    _forEach(this._dmSnsMsgSettingExList, (v, idx) => v.dmSnsMsgSetting.itemNumber = idx + 1);
    this.blFormGroup.reset();
    this.applyRequiredInputDiv();
    this.notifyChange();

    _defer(() => {
      const menu = this.contextMenuList.find((_, idx) => idx === index);
      if (!_isNil(menu)) {
        menu.nativeElement.focus();
      }
    });
  }

  /**
   * 項目の上移動を行います。
   * @param index インデックス
   */
  private moveUpDmSnsMsgSetting(index: number) {
    if (index > 0) {
      this._dmSnsMsgSettingExList = _forEach(_concat(
        _slice(this._dmSnsMsgSettingExList, 0, index - 1),
        _find(this._dmSnsMsgSettingExList, (_, idx) => idx === index),
        _find(this._dmSnsMsgSettingExList, (_, idx) => idx === index - 1),
        _slice(this._dmSnsMsgSettingExList, index + 1)
      ), (v, idx) => v.dmSnsMsgSetting.itemNumber = idx + 1);

      // バリデーション設定
      const keys = _filter(Object.keys(this.blFormGroup.validatorGroup), key =>
        key.startsWith('DmSnsMsgSetting#') || key.startsWith('DmSnsMsgSettingDetail#')
      );
      for (const key of keys) {
        let validatorList = this.blFormGroup.getArrayValidator(key);
        validatorList = _concat(
          _slice(validatorList, 0, index - 1),
          _find(validatorList, (_, idx) => idx === index),
          _find(validatorList, (_, idx) => idx === index - 1),
          _slice(validatorList, index + 1)
        );
        this.blFormGroup.setArrayValidator(key, validatorList);
      }
      this.applyRequiredInputDiv();
      this.notifyChange();

      _defer(() => {
        const menu = this.contextMenuList.find((_, idx) => idx === index - 1);
        if (!_isNil(menu)) {
          menu.nativeElement.focus();
        }
      });
    }
  }

  /**
   * 項目の下移動を行います。
   * @param index インデックス
   */
  private moveDownDmSnsMsgSetting(index: number) {
    if (index < this._dmSnsMsgSettingExList.length - 1) {
      this._dmSnsMsgSettingExList = _forEach(_concat(
        _slice(this._dmSnsMsgSettingExList, 0, index),
        _find(this._dmSnsMsgSettingExList, (_, idx) => idx === index + 1),
        _find(this._dmSnsMsgSettingExList, (_, idx) => idx === index),
        _slice(this._dmSnsMsgSettingExList, index + 2)
      ), (v, idx) => v.dmSnsMsgSetting.itemNumber = idx + 1);

      // バリデーション設定
      const keys = _filter(Object.keys(this.blFormGroup.validatorGroup), key =>
        key.startsWith('DmSnsMsgSetting#') || key.startsWith('DmSnsMsgSettingDetail#')
      );
      for (const key of keys) {
        let validatorList = this.blFormGroup.getArrayValidator(key);
        validatorList = _concat(
          _slice(validatorList, 0, index),
          _find(validatorList, (_, idx) => idx === index + 1),
          _find(validatorList, (_, idx) => idx === index),
          _slice(validatorList, index + 2)
        );
      }
      this.applyRequiredInputDiv();
      this.notifyChange();
    }
  }

  /**
   * 行追加イベントハンドラ
   */
  onAddDmSnsMsgSetting() {
    this.addDmSnsMsgSetting(this._dmSnsMsgSettingExList.length);
  }

  /**
   * DMSNSメッセージ本文タイプ変更イベントハンドラ
   * @param dmSnsMsgBodyTypeDiv DMSNSメッセージ本文タイプ区分
   * @param dmSnsMsgSetting DMSNSメッセージ設定
   * @param index インデックス
   */
  onChangeDmSnsMsgBodyType(dmSnsMsgBodyTypeDiv: SnsMsgBodyType, dmSnsMsgSetting: IDmSnsMsgSetting, index: number) {
    if (dmSnsMsgBodyTypeDiv === SnsMsgBodyType.Survey && !this.isAvailableSurvey) {
      return;
    }
    dmSnsMsgSetting.dmSnsMsgBodyTypeDiv = dmSnsMsgBodyTypeDiv;
    // 初期化
    const def = _cloneDeep(DefaultDmSnsMsgSettingEx);
    if (dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Text) {
      this._dmSnsMsgSettingExList[index].file = def.file;
      this._dmSnsMsgSettingExList[index].count = def.count;
      this._dmSnsMsgSettingExList[index].isStyleDisabled = def.isStyleDisabled;
      dmSnsMsgSetting.lineMsgTemplateType = def.dmSnsMsgSetting.lineMsgTemplateType;
      dmSnsMsgSetting.dmSnsMsgDispFile = def.dmSnsMsgSetting.dmSnsMsgDispFile;
      dmSnsMsgSetting.snsImageUrl = def.dmSnsMsgSetting.snsImageUrl;
      dmSnsMsgSetting.snsImageId = def.dmSnsMsgSetting.snsImageId;
      dmSnsMsgSetting.snsThumbnailImageUrl = def.dmSnsMsgSetting.snsThumbnailImageUrl;
      dmSnsMsgSetting.snsThumbnailImageId = def.dmSnsMsgSetting.snsThumbnailImageId;
      dmSnsMsgSetting.dmSnsMsgSettingDetailList = def.dmSnsMsgSetting.dmSnsMsgSettingDetailList;
      this.clearSurvey(index, dmSnsMsgSetting);
    } else if (dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.FlexMessage) {
      if (_isEmpty(dmSnsMsgSetting.dmSnsMsgSettingDetailList)) {
        dmSnsMsgSetting.dmSnsMsgSettingDetailList.push(_cloneDeep(DefaultDmSnsMsgSettingDetail));
      }
      this._dmSnsMsgSettingExList[index].file = def.file;
      this._dmSnsMsgSettingExList[index].count = def.count;
      this._dmSnsMsgSettingExList[index].isStyleDisabled = def.isStyleDisabled;
      this._dmSnsMsgSettingExList[index].dispLineMsgTemplateName = def.dispLineMsgTemplateName;
      this._dmSnsMsgSettingExList[index].lineMsgTemplateImageUrl = def.lineMsgTemplateImageUrl;
      dmSnsMsgSetting.dmSnsMsgBody = def.dmSnsMsgSetting.dmSnsMsgBody;
      dmSnsMsgSetting.dmSnsMsgDispFile = def.dmSnsMsgSetting.dmSnsMsgDispFile;
      dmSnsMsgSetting.snsImageUrl = def.dmSnsMsgSetting.snsImageUrl;
      dmSnsMsgSetting.snsImageId = def.dmSnsMsgSetting.snsImageId;
      dmSnsMsgSetting.snsThumbnailImageUrl = def.dmSnsMsgSetting.snsThumbnailImageUrl;
      dmSnsMsgSetting.snsThumbnailImageId = def.dmSnsMsgSetting.snsThumbnailImageId;
      dmSnsMsgSetting.dmSnsMsgNotifyDispTitle = def.dmSnsMsgSetting.dmSnsMsgNotifyDispTitle;
      dmSnsMsgSetting.lineMsgTemplateType = def.dmSnsMsgSetting.lineMsgTemplateType;
      dmSnsMsgSetting.lineMsgTemplateTypeEnumName = def.dmSnsMsgSetting.lineMsgTemplateTypeEnumName;
      // 入力可能項目を反映
      this.setEnabledItemInFlex();
      this.updateLineMsgTemplateUrlList();
      this.setLineMsgTemplateInfoInFlex();
      this.clearSurvey(index, dmSnsMsgSetting);
    } else if (dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Image) {
      this._dmSnsMsgSettingExList[index].count = def.count;
      this._dmSnsMsgSettingExList[index].isStyleDisabled = def.isStyleDisabled;
      dmSnsMsgSetting.lineMsgTemplateType = def.dmSnsMsgSetting.lineMsgTemplateType;
      dmSnsMsgSetting.dmSnsMsgBody = def.dmSnsMsgSetting.dmSnsMsgBody;
      dmSnsMsgSetting.dmSnsMsgSettingDetailList = def.dmSnsMsgSetting.dmSnsMsgSettingDetailList;
      this.clearSurvey(index, dmSnsMsgSetting);
    } else if (dmSnsMsgBodyTypeDiv === SnsMsgBodyType.Survey) {
      this._dmSnsMsgSettingExList[index].file = def.file;
      this._dmSnsMsgSettingExList[index].count = def.count;
      this._dmSnsMsgSettingExList[index].isStyleDisabled = def.isStyleDisabled;
      this.clearSurvey(index, dmSnsMsgSetting);
      dmSnsMsgSetting.lineMsgTemplateType = def.dmSnsMsgSetting.lineMsgTemplateType;
      dmSnsMsgSetting.dmSnsMsgBody = def.dmSnsMsgSetting.dmSnsMsgBody;
      dmSnsMsgSetting.dmSnsMsgDispFile = def.dmSnsMsgSetting.dmSnsMsgDispFile;
      dmSnsMsgSetting.snsImageUrl = def.dmSnsMsgSetting.snsImageUrl;
      dmSnsMsgSetting.snsImageId = def.dmSnsMsgSetting.snsImageId;
      dmSnsMsgSetting.snsThumbnailImageUrl = def.dmSnsMsgSetting.snsThumbnailImageUrl;
      dmSnsMsgSetting.snsThumbnailImageId = def.dmSnsMsgSetting.snsThumbnailImageId;
      dmSnsMsgSetting.dmSnsMsgSettingDetailList = def.dmSnsMsgSetting.dmSnsMsgSettingDetailList;
    }
    this.applyRequiredInputDiv();
    this.notifyChange();
  }

  /**
   * アンケート選択をクリアする
   * @param index インデックス
   * @param dmSnsMsgSetting DMSNSメッセージ設定
   */
  private clearSurvey(index: number, dmSnsMsgSetting: IDmSnsMsgSetting) {
    const def = _cloneDeep(DefaultDmSnsMsgSettingEx);
    this._dmSnsMsgSettingExList[index].survey = def.survey;
    this.clearSurveyOnSetting(<IDmSnsMsgSettingEx>dmSnsMsgSetting);
  }

  /**
   * DMSNSメッセージ設定のアンケート項目をクリアする
   * アンケート未設定の案内文へ項目を追加しないよう、値を持たない場合は書き換えない
   * @param dmSnsMsgSetting DMSNSメッセージ設定
   */
  private clearSurveyOnSetting(dmSnsMsgSetting: IDmSnsMsgSettingEx) {
    if (!_isNil(dmSnsMsgSetting.surveyNo)) {
      dmSnsMsgSetting.surveyNo = null;
    }
    if (!_isNil(dmSnsMsgSetting.surveyName)) {
      dmSnsMsgSetting.surveyName = '';
    }
  }

  /**
   * アンケート選択変更イベントハンドラ
   * @param survey 選択したアンケート
   * @param index インデックス
   */
  onChangeSurvey(survey: ISurveyGuideLabelValue, index: number) {
    this._dmSnsMsgSettingExList[index].survey = survey || {};
    this._dmSnsMsgSettingExList[index].dmSnsMsgSetting.surveyNo = survey ? survey.surveyNo : null;
    this._dmSnsMsgSettingExList[index].dmSnsMsgSetting.surveyName = survey ? survey.surveyName : '';
    this.notifyChange();
  }

  /**
   * 他の項目でアンケートを選択中の場合、アンケート欄を非活性にする
   * @param index インデックス
   * @returns 非活性の場合true
   */
  isSurveyFieldDisabled(index: number): boolean {
    const firstSurveyIndex = _findIndex(this._dmSnsMsgSettingExList,
      ex => ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === SnsMsgBodyType.Survey);
    return firstSurveyIndex !== -1 && firstSurveyIndex !== index;
  }

  /**
   * LINEメッセージテンプレートガイド表示イベントハンドラ
   * @param index インデックス
   * @param el 起動エレメント
   */
  onShowLineMsgTemplateGuide(index: number, el: HTMLElement) {
    this.modalService.show(
      LineMsgTemplateGuideComponent,
      { data: { initValue: this._dmSnsMsgSettingExList[index].dmSnsMsgSetting.lineMsgTemplateType } }
    ).subscribe(
      modalRef => {
        modalRef.hide(el);
        switch (modalRef.reason) {
          case ModalReason.Done:
            const result: ILineMsgTemplate = modalRef.getResults();
            this.selectLineMsgTemplate(result.lineMsgTemplateType, index);
            this.updateLineMsgTemplateUrlList();
            this.setLineMsgTemplateInfoInFlex();
            break;
        }
      }
    );
  }

  /**
   * タイプ選択イベント
   * @param lineMsgTemplateType LINEメッセージテンプレートタイプ
   * @param index インデックス
   */
  selectLineMsgTemplate(lineMsgTemplateType: string, index: number) {
    // LINEメッセージテンプレート
    const result = _find(this.lineMsgTemplateList, v => v.lineMsgTemplateType === lineMsgTemplateType);
    const dmSnsMsgSetting = this._dmSnsMsgSettingExList[index].dmSnsMsgSetting;
    dmSnsMsgSetting.lineMsgTemplateType = result.lineMsgTemplateType;
    dmSnsMsgSetting.lineMsgTemplateTypeEnumName = result.lineMsgTemplateTypeEnumName;
    // 入力可能項目を反映
    this.setEnabledItemInFlex();
    if (!_isEmpty(this._dmSnsMsgSettingExList[index].dmSnsMsgSetting.dmSnsMsgSettingDetailList)) {
      const dmSnsMsgSettingDetail = _head(this._dmSnsMsgSettingExList[index].dmSnsMsgSetting.dmSnsMsgSettingDetailList);
      if (!this._dmSnsMsgSettingExList[index].isEnabledSetImageInFlex) {
        this._dmSnsMsgSettingExList[index].file = null;
        const def = _cloneDeep(DefaultDmSnsMsgSettingDetail);
        dmSnsMsgSettingDetail.dmSnsMsgDispFile = def.dmSnsMsgDispFile;
      }
      // DMSNSメッセージ件名
      if (_isEmpty(dmSnsMsgSettingDetail.dmSnsMsgTitle) && !_isEmpty(result.dmSnsMsgTitle)) {
        // APIでエスケープされてる改行コードを戻して本文に反映
        dmSnsMsgSettingDetail.dmSnsMsgTitle = result.dmSnsMsgTitle.replace('\\n', '\n');
      }
      // DMSNSメッセージ通知表示タイトル
      if (_isEmpty(dmSnsMsgSetting.dmSnsMsgNotifyDispTitle) && !_isEmpty(result.dmSnsMsgTitle)) {
        // APIでエスケープされてる改行コードを削除して本文に反映
        dmSnsMsgSetting.dmSnsMsgNotifyDispTitle = result.dmSnsMsgTitle.replace('\\n', '');
      }
      // DMSNSメッセージ本文
      if (_isEmpty(dmSnsMsgSettingDetail.dmSnsMsgBody) && !_isEmpty(result.dmSnsMsgBody)) {
        // APIでエスケープされてる改行コードを戻して本文に反映
        dmSnsMsgSettingDetail.dmSnsMsgBody = result.dmSnsMsgBody.replace(new RegExp(/\\n/, 'g'), '\n');
      }
      // DMSNSメッセージボタン表示名称
      if (_isEmpty(dmSnsMsgSettingDetail.dmSnsMsgBtnDispName) && !_isEmpty(result.dmSnsMsgBtnDispName)) {
        dmSnsMsgSettingDetail.dmSnsMsgBtnDispName = result.dmSnsMsgBtnDispName;
      }
    }
    // 文字数カウント更新
    this.updateWordCount();
    this.notifyChange();
  }

  /**
   * 画像押下時の挙動変更イベント
   * @param dmSnsMsgSettingDetail DMSNSメッセージ設定詳細
   */
  onChangeSnsMsgImageActionDiv(dmSnsMsgSettingDetail: IDmSnsMsgSettingDetail) {
    const def = _cloneDeep(DefaultDmSnsMsgSettingDetail);
    dmSnsMsgSettingDetail.dmSnsMsgImageSendText = def.dmSnsMsgImageSendText;
    dmSnsMsgSettingDetail.dmSnsMsgImageTransitionUrl = def.dmSnsMsgImageTransitionUrl;
    this.applyRequiredInputDiv();
    this.notifyChange();
  }

  /**
   * 「画像押下を有効にする」値変更イベント
   * @param $event イベント
   * @param dmSnsMsgSetting DMSNSメッセージ設定
   */
  onChangeImageActionValidFlag($event: any, dmSnsMsgSetting: IDmSnsMsgSetting) {
    _head(dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgImageActionValidFlag = $event.target.checked;
    const def = _cloneDeep(DefaultDmSnsMsgSettingDetail);
    _head(dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgImageSendText = def.dmSnsMsgImageSendText;
    _head(dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgImageTransitionUrl = def.dmSnsMsgImageTransitionUrl;
    this.applyRequiredInputDiv();
    this.notifyChange();
  }

  /**
   * ボタン押下時の挙動変更イベント
   * @param dmSnsMsgSettingDetail DMSNSメッセージ設定詳細
   */
  onChangeSnsMsgBtnActionDiv(dmSnsMsgSettingDetail: IDmSnsMsgSettingDetail) {
    const def = _cloneDeep(DefaultDmSnsMsgSettingDetail);
    dmSnsMsgSettingDetail.dmSnsMsgBtnSendText = def.dmSnsMsgBtnSendText;
    dmSnsMsgSettingDetail.dmSnsMsgTransitionUrl = def.dmSnsMsgTransitionUrl;
    this.applyRequiredInputDiv();
    this.notifyChange();
  }

  /**
   * 「ボタンを有効にする」値変更イベント
   * @param $event イベント
   * @param dmSnsMsgSetting DMSNSメッセージ設定
   */
  onChangeBtnDispFlag($event: any, dmSnsMsgSetting: IDmSnsMsgSetting) {
    _head(dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgBtnDispFlag = $event.target.checked;
    const def = _cloneDeep(DefaultDmSnsMsgSettingDetail);
    _head(dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgBtnDispName = def.dmSnsMsgBtnDispName;
    _head(dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgBtnSendText = def.dmSnsMsgBtnSendText;
    _head(dmSnsMsgSetting.dmSnsMsgSettingDetailList).dmSnsMsgTransitionUrl = def.dmSnsMsgTransitionUrl;
    this.applyRequiredInputDiv();
    this.notifyChange();
  }

  /**
   * 変更通知
   */
  notifyChange() {
    _forEach(this._dmSnsMsgSettingExList, ex => {
      if (ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === SnsMsgBodyType.Survey) {
        ex.dmSnsMsgSetting.surveyNo = ex.survey ? ex.survey.surveyNo : null;
        ex.dmSnsMsgSetting.surveyName = ex.survey ? ex.survey.surveyName : '';
      } else {
        this.clearSurveyOnSetting(ex.dmSnsMsgSetting);
      }
    });
    this.updateDmSnsMsgSettingEvent.emit(this._dmSnsMsgSettingExList);
  }

  /**
   * 画像追加イベント
   * @param file 画像ファイル
   * @param index インデックス
   */
  onAppendImage(file: File, index: number) {
    this._dmSnsMsgSettingExList[index].file = file;
    this.notifyChange();
  }

  /**
   * 画像削除イベント(画像セル)
   * @param index インデックス
   */
  onRemoveImageCell(index: number) {
    this._dmSnsMsgSettingExList[index].file = null;
    this._dmSnsMsgSettingExList[index].dmSnsMsgSetting.dmSnsMsgDispFile = DefaultDmSnsMsgSetting.dmSnsMsgDispFile;
    this._dmSnsMsgSettingExList[index].dmSnsMsgSetting.snsImageId = DefaultDmSnsMsgSetting.snsImageId;
    this._dmSnsMsgSettingExList[index].dmSnsMsgSetting.snsImageUrl = DefaultDmSnsMsgSetting.snsImageUrl;
    this.notifyChange();
  }

  /**
   * 画像削除イベント(フレックスメッセージ)
   * @param index インデックス
   */
  onRemoveImageFlex(index: number) {
    this._dmSnsMsgSettingExList[index].file = null;
    this._dmSnsMsgSettingExList[index].dmSnsMsgSetting.dmSnsMsgSettingDetailList[0].dmSnsMsgDispFile
      = DefaultDmSnsMsgSettingDetail.dmSnsMsgDispFile;
    this._dmSnsMsgSettingExList[index].dmSnsMsgSetting.dmSnsMsgSettingDetailList[0].snsImageId
      = DefaultDmSnsMsgSettingDetail.snsImageId;
    this._dmSnsMsgSettingExList[index].dmSnsMsgSetting.dmSnsMsgSettingDetailList[0].snsImageUrl
      = DefaultDmSnsMsgSettingDetail.snsImageUrl;
    this.notifyChange();
  }

  /**
   * 画像保存
   */
  public saveAttachments(): Observable<IDmSnsMsgSetting[]> {
    return ObservableForkJoin([this.saveImageCellAttachments(), this.saveRetchMessageAttachments()]).pipe(map(([v, w]) => v.concat(w)));
  }

  /**
   * 画像保存 (フレックスメッセージセル)
   */
  public saveRetchMessageAttachments(): Observable<IDmSnsMsgSetting[]> {
    return ObservableOf(null).pipe(mergeMap(() => {
      // 画像アップロードがあるデータのみにフィルタリング
      const filtered = _filter(this._dmSnsMsgSettingExList,
        (ex) => ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.FlexMessage
          && ex.isEnabledSetImageInFlex);
      // filteredとフレックスメッセージのAttachmentComponent indexが一致するので順次アップロード
      const uploads: Observable<IDmSnsMsgSetting>[] = _map(filtered, (ex, index) => {
        const dmSnsMsgSetting = ex.dmSnsMsgSetting;
        const attachment = this.flexMsgCellAttachmentList.toArray()[index];
        // 画像ファイルにfileManageIdが無い場合ファイルをクリア
        if (!_isNil(dmSnsMsgSetting.dmSnsMsgSettingDetailList[0].dmSnsMsgDispFile) &&
          _isNil(dmSnsMsgSetting.dmSnsMsgSettingDetailList[0].dmSnsMsgDispFile.fileManageId)) {
          dmSnsMsgSetting.dmSnsMsgSettingDetailList[0].dmSnsMsgDispFile = null;
          attachment.clear();
        }
        // 画像保存
        return attachment.save(FilePublicDiv.Public).pipe(
          map(res => {
            dmSnsMsgSetting.dmSnsMsgSettingDetailList[0].dmSnsMsgDispFile = null;
            if (_isArray(res) && res.length > 0 && !_isNil(_head(res).fileManageId)) {
              dmSnsMsgSetting.dmSnsMsgSettingDetailList[0].dmSnsMsgDispFile = _head(res);
            }
            return dmSnsMsgSetting;
          })
        );
      });
      if (_isEmpty(uploads)) {
        return ObservableOf([]);
      }
      return ObservableForkJoin(uploads);
    }));
  }


  /**
   * 画像保存 (画像セル)
   */
  private saveImageCellAttachments(): Observable<IDmSnsMsgSetting[]> {
    return ObservableOf(null).pipe(mergeMap(() => {
      const filtered = _filter(this._dmSnsMsgSettingExList, (ex) => ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Image);
      const uploads: Observable<IDmSnsMsgSetting>[] = _map(filtered, (ex, index) => {
        const dmSnsMsgSetting = ex.dmSnsMsgSetting;
        const attachment = this.imageCellAttachmentList.toArray()[index];
        // 画像ファイルにfileManageIdが無い場合ファイルをクリア
        if (!_isNil(dmSnsMsgSetting.dmSnsMsgDispFile) && _isNil(dmSnsMsgSetting.dmSnsMsgDispFile.fileManageId)) {
          dmSnsMsgSetting.dmSnsMsgDispFile = null;
          attachment.clear();
        }
        // 画像保存
        return attachment.save(FilePublicDiv.Public).pipe(
          map(res => {
            dmSnsMsgSetting.dmSnsMsgDispFile = null;
            if (_isArray(res) && res.length > 0 && !_isNil(_head(res).fileManageId)) {
              dmSnsMsgSetting.dmSnsMsgDispFile = _head(res);
            }
            return dmSnsMsgSetting;
          })
        );
      });
      if (_isEmpty(uploads)) {
        return ObservableOf([]);
      }
      return ObservableForkJoin(uploads);
    }));
  }

  /**
   * バリデータ取得
   * @param key キー
   * @param index インデックス
   * @returns バリデータ
   */
  getValidator(key: string, index: number): BlValidator | null {
    if (this.blFormGroup.validatorGroup[key]) {
      return this.blFormGroup.validatorGroup[key][index];
    }
    return null;
  }

}



// WEBPACK FOOTER //
// ./src/app/feature/sns-send-message-modal/content/sns-send-message-content.component.ts